import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';

interface SelectionContextType {
  selectedPolygonId: string | null;
  setSelectedPolygonId: (id: string | null) => void;
  selectedLatLng: { lat: number; lng: number } | null;
  setSelectedLatLng: (latLng: { lat: number; lng: number } | null) => void;
}

const SelectionContext = createContext<SelectionContextType>({
  selectedPolygonId: null,
  setSelectedPolygonId: () => {},
  selectedLatLng: null,
  setSelectedLatLng: () => {},
});

export const useSelection = () => useContext(SelectionContext);

export const SelectionProvider: React.FC<{
  children: React.ReactNode;
  /**
   * Seeds the selected point at mount. Used where a location is already known
   * before the map is shown — e.g. the create-project wizard resuming its
   * Location step, or arriving there from the listing map's location picker —
   * so the pin and derived bounding box render straight away.
   */
  initialLatLng?: { lat: number; lng: number } | null;
}> = ({ children, initialLatLng = null }) => {
  const [selectedPolygonId, setSelectedPolygonIdState] = useState<string | null>(null);
  const [selectedLatLng, setSelectedLatLng] = useState<{ lat: number; lng: number } | null>(
    initialLatLng
  );

  // Coerce to string defensively — some callers pass numeric feature IDs
  // (e.g. treatment IDs), which would later break consumers that call
  // string methods like .match() on selectedPolygonId.
  const setSelectedPolygonId = useCallback((id: string | null) => {
    setSelectedPolygonIdState(id == null ? null : String(id));
  }, []);

  const value = useMemo(
    () => ({
      selectedPolygonId,
      setSelectedPolygonId,
      selectedLatLng,
      setSelectedLatLng,
    }),
    [selectedPolygonId, setSelectedPolygonId, selectedLatLng],
  );

  return (
    <SelectionContext.Provider value={value}>
      {children}
    </SelectionContext.Provider>
  );
};

export default SelectionContext;
